import React, { useEffect, useState } from 'react';
import axios from 'axios';

interface StorePageProps {
  store: any;
  onBack: () => void;
  onAddToCart: (item: any, storeId: string, storeName: string) => void;
  cart: any[];
  onCheckout: () => void;
}

const API_URL = 'https://api.bringit.co.in/api';

const StorePage: React.FC<StorePageProps> = ({ store, onBack, onAddToCart, cart, onCheckout }) => {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredItems, setFilteredItems] = useState<any[]>([]);
  const [selectedVariants, setSelectedVariants] = useState<any>({});
  const [customQuantities, setCustomQuantities] = useState<any>({});
  const [showCategoryWarning, setShowCategoryWarning] = useState(false);
  const [pendingItem, setPendingItem] = useState<any>(null);
  
  // Custom Order State
  const [showCustomOrder, setShowCustomOrder] = useState(false);
  const [customOrderDescription, setCustomOrderDescription] = useState('');
  const [customItems, setCustomItems] = useState<{name: string, quantity: string, notes: string}[]>([
    { name: '', quantity: '', notes: '' }
  ]);
  const [customOrderNotes, setCustomOrderNotes] = useState('');
  const [customOrderSubmitting, setCustomOrderSubmitting] = useState(false);

  useEffect(() => {
    fetchItems();
  }, [store._id]);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredItems(items);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = items.filter(item => 
        item.name.toLowerCase().includes(query) ||
        item.nameInTelugu?.toLowerCase().includes(query) ||
        item.category?.toLowerCase().includes(query)
      );
      setFilteredItems(filtered);
    }
  }, [searchQuery, items]);

  const fetchItems = async () => {
    try {
      setLoading(true);
      // Fetch menu items from the specific store
      const response = await axios.get(`${API_URL}/stores/${store._id}/menu`);
      const menuItems = response.data.menuItems || [];
      setItems(menuItems);
      setFilteredItems(menuItems);
    } catch (error) {
      console.error('Error fetching items:', error);
      setItems([]);
      setFilteredItems([]);
    } finally {
      setLoading(false);
    }
  };

  const getItemQuantityInCart = (itemId: string, variantIndex?: number) => {
    const cartItem = cart.find(ci => ci._id === itemId && ci.variantIndex === variantIndex);
    return cartItem ? cartItem.quantity : 0;
  };

  const selectVariant = (itemId: string, index: number) => {
    setSelectedVariants({ ...selectedVariants, [itemId]: index });
  };

  const handleCustomQuantity = (itemId: string, value: string) => {
    setCustomQuantities({ ...customQuantities, [itemId]: value });
  };

  const addCustomQuantityToCart = (item: any) => {
    const customQty = customQuantities[item._id];
    if (customQty && parseFloat(customQty) > 0) {
      const itemWithCustomQty = {
        ...item,
        customQuantity: customQty,
        quantity: 1
      };
      checkCategoryAndAdd(itemWithCustomQty);
      setCustomQuantities({ ...customQuantities, [item._id]: '' });
    }
  };

  const handleAddToCartWithCheck = (item: any) => {
    // For decrement operations (negative quantity), call directly
    if (item.quantity < 0) {
      const itemWithCategory = { ...item, storeCategory: store.category };
      onAddToCart(itemWithCategory, store._id, store.name);
      return;
    }

    // For increment operations, check category
    checkCategoryAndAdd(item);
  };

  const checkCategoryAndAdd = (item: any) => {
    // Check if cart has items from other stores
    const otherStoreItems = cart.filter((cartItem: any) => cartItem.storeId !== store._id);
    
    // If cart is empty or only has items from this store, add directly
    if (otherStoreItems.length === 0) {
      const itemWithCategory = { ...item, storeCategory: store.category };
      onAddToCart(itemWithCategory, store._id, store.name);
      return;
    }
    
    // If cart has items from other stores, show warning
    setPendingItem(item);
    setShowCategoryWarning(true);
  };

  const handleConfirmAddCategory = () => {
    if (pendingItem) {
      const itemWithCategory = { ...pendingItem, storeCategory: store.category };
      onAddToCart(itemWithCategory, store._id, store.name);
    }
    setShowCategoryWarning(false);
    setPendingItem(null);
  };

  const handleSeparateOrder = () => {
    // User chose separate order - just add the item normally
    if (pendingItem) {
      const itemWithCategory = { ...pendingItem, storeCategory: store.category };
      onAddToCart(itemWithCategory, store._id, store.name);
    }
    setShowCategoryWarning(false);
    setPendingItem(null);
  };

  const handleCancelAddCategory = () => {
    setShowCategoryWarning(false);
    setPendingItem(null);
  };

  // Custom Order Functions
  const addCustomItemRow = () => {
    setCustomItems([...customItems, { name: '', quantity: '', notes: '' }]);
  };

  const removeCustomItemRow = (index: number) => {
    if (customItems.length > 1) {
      setCustomItems(customItems.filter((_, i) => i !== index));
    }
  };

  const updateCustomItem = (index: number, field: string, value: string) => {
    const updated = [...customItems];
    updated[index] = { ...updated[index], [field]: value };
    setCustomItems(updated);
  };

  const submitCustomOrder = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Please login to place a custom order');
      return;
    }

    const validItems = customItems.filter(item => item.name.trim());
    if (validItems.length === 0 && !customOrderDescription.trim()) {
      alert('Please add at least one item or describe what you need');
      return;
    }

    setCustomOrderSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/orders/custom`, {
        storeId: store._id,
        storeName: store.name,
        customDescription: customOrderDescription,
        requestedItems: validItems,
        customerNotes: customOrderNotes,
        deliveryAddress: { address: 'Will be updated' }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('✅ Custom order request sent!\n\nThe store will review your request and send you a bill. You will receive a notification when the bill is ready.');
      setShowCustomOrder(false);
      setCustomOrderDescription('');
      setCustomItems([{ name: '', quantity: '', notes: '' }]);
      setCustomOrderNotes('');
    } catch (error: any) {
      console.error('Error submitting custom order:', error);
      alert(error.response?.data?.message || 'Failed to submit custom order');
    } finally {
      setCustomOrderSubmitting(false);
    }
  };

  const getStoreCartItems = () => {
    return cart.filter(item => item.storeId === store._id);
  };

  const getCartTotal = () => {
    const storeItems = getStoreCartItems();
    return storeItems.reduce((sum, item) => {
      const price = item.selectedVariant ? item.selectedVariant.price : item.price;
      return sum + (price * item.quantity);
    }, 0);
  };

  const getCategoryIcon = (category: string) => {
    const icons: any = {
      grocery: '🛒',
      pharmacy: '💊',
      vegetables: '🥬',
      meat: '🥩',
      bakery: '🍞',
      dairy: '🥛',
      restaurant: '🍽️',
      sweets: '🍬',
      beverages: '🥤',
      fruits: '🍎',
      snacks: '🍪',
    };
    return icons[category] || '🏪';
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '15px',
        color: 'white',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '15px' }}>
          <button
            onClick={onBack}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px',
              cursor: 'pointer',
              color: 'white'
            }}
          >
            ←
          </button>
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700' }}>{store.name}</h2>
            <p style={{ margin: '4px 0 0 0', fontSize: '13px', opacity: 0.9 }}>
              📍 {store.location.address}
            </p>
          </div>
        </div>

        {/* Search Bar */}
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            placeholder="Search items... • వస్తువులు వెతకండి..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 45px 12px 45px',
              borderRadius: '25px',
              border: 'none',
              fontSize: '15px',
              outline: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}
          />
          <span style={{
            position: 'absolute',
            left: '15px',
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: '20px'
          }}>
            🔍
          </span>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                fontSize: '20px',
                cursor: 'pointer',
                padding: '5px'
              }}
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Items List */}
      <div style={{ padding: '15px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ 
              display: 'inline-block',
              width: '40px', 
              height: '40px', 
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #667eea',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
          </div>
        ) : filteredItems.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <span style={{ fontSize: '60px' }}>📦</span>
            <h3 style={{ color: '#666', marginTop: '15px' }}>
              {searchQuery ? 'No items found' : 'No items available'}
            </h3>
            <p style={{ color: '#999', fontSize: '14px' }}>
              {searchQuery ? 'Try searching with different keywords' : 'Check back later for items'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '12px' }}>
            {filteredItems.map((item) => {
              const quantityInCart = getItemQuantityInCart(item._id);
              return (
                <div
                  key={item._id}
                  style={{
                    background: 'white',
                    borderRadius: '12px',
                    padding: '15px',
                    display: 'flex',
                    gap: '15px',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                    border: '1px solid #f0f0f0'
                  }}
                >
                  <div style={{
                    width: '80px',
                    height: '80px',
                    background: `linear-gradient(135deg, ${item.color || '#667eea'}, ${item.color || '#764ba2'})`,
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '40px',
                    flexShrink: 0
                  }}>
                    {item.icon || getCategoryIcon(item.category)}
                  </div>

                  <div style={{ flex: 1 }}>
                    <h3 style={{ margin: '0 0 5px 0', fontSize: '16px', fontWeight: '700', color: '#333' }}>
                      {item.name}
                    </h3>
                    {item.nameInTelugu && (
                      <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#666' }}>
                        {item.nameInTelugu}
                      </p>
                    )}

                    {/* Variants Selection */}
                    {item.variants && item.variants.length > 0 ? (
                      <div style={{ marginTop: '10px' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
                          {item.variants.map((variant: any, idx: number) => {
                            const isSelected = selectedVariants[item._id] === idx;
                            const variantQty = getItemQuantityInCart(item._id, idx);
                            return (
                              <button
                                key={idx}
                                onClick={() => selectVariant(item._id, idx)}
                                style={{
                                  padding: '6px 12px',
                                  borderRadius: '8px',
                                  border: isSelected ? '2px solid #667eea' : '1px solid #ddd',
                                  background: isSelected ? '#f0f4ff' : 'white',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  color: isSelected ? '#667eea' : '#666'
                                }}
                              >
                                {variant.quantity} {variant.unit} - ₹{variant.price}
                              </button>
                            );
                          })}
                        </div>

                        {selectedVariants[item._id] !== undefined && (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span style={{ fontSize: '16px', fontWeight: '700', color: '#667eea' }}>
                              ₹{item.variants[selectedVariants[item._id]].price}
                            </span>
                            {getItemQuantityInCart(item._id, selectedVariants[item._id]) > 0 ? (
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                borderRadius: '8px',
                                padding: '5px'
                              }}>
                                <button
                                  onClick={() => handleAddToCartWithCheck({...item, quantity: -1, variantIndex: selectedVariants[item._id], selectedVariant: item.variants[selectedVariants[item._id]]})}
                                  style={{
                                    background: 'rgba(255,255,255,0.2)',
                                    border: 'none',
                                    borderRadius: '6px',
                                    width: '30px',
                                    height: '30px',
                                    color: 'white',
                                    fontSize: '18px',
                                    cursor: 'pointer',
                                    fontWeight: '700'
                                  }}
                                >
                                  -
                                </button>
                                <span style={{ color: 'white', fontWeight: '700', minWidth: '20px', textAlign: 'center' }}>
                                  {getItemQuantityInCart(item._id, selectedVariants[item._id])}
                                </span>
                                <button
                                  onClick={() => handleAddToCartWithCheck({...item, quantity: 1, variantIndex: selectedVariants[item._id], selectedVariant: item.variants[selectedVariants[item._id]]})}
                                  style={{
                                    background: 'rgba(255,255,255,0.2)',
                                    border: 'none',
                                    borderRadius: '6px',
                                    width: '30px',
                                    height: '30px',
                                    color: 'white',
                                    fontSize: '18px',
                                    cursor: 'pointer',
                                    fontWeight: '700'
                                  }}
                                >
                                  +
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => handleAddToCartWithCheck({...item, quantity: 1, variantIndex: selectedVariants[item._id], selectedVariant: item.variants[selectedVariants[item._id]]})}
                                style={{
                                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                  border: 'none',
                                  borderRadius: '8px',
                                  padding: '8px 20px',
                                  color: 'white',
                                  fontSize: '14px',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
                                }}
                              >
                                Add +
                              </button>
                            )}
                          </div>
                        )}

                        {/* Custom Quantity Option */}
                        <div style={{ marginTop: '10px', padding: '10px', background: '#f8f9fa', borderRadius: '8px' }}>
                          <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: '#666', fontWeight: '600' }}>
                            📝 Custom Quantity
                          </p>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <input
                              type="text"
                              placeholder="e.g., 750 gm, 1.5 kg"
                              value={customQuantities[item._id] || ''}
                              onChange={(e) => handleCustomQuantity(item._id, e.target.value)}
                              style={{
                                flex: 1,
                                padding: '8px',
                                borderRadius: '6px',
                                border: '1px solid #ddd',
                                fontSize: '13px'
                              }}
                            />
                            <button
                              onClick={() => addCustomQuantityToCart(item)}
                              disabled={!customQuantities[item._id]}
                              style={{
                                padding: '8px 16px',
                                borderRadius: '6px',
                                border: 'none',
                                background: customQuantities[item._id] ? '#667eea' : '#ddd',
                                color: 'white',
                                fontSize: '13px',
                                fontWeight: '600',
                                cursor: customQuantities[item._id] ? 'pointer' : 'not-allowed'
                              }}
                            >
                              Add
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      // Original price display for items without variants
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '10px' }}>
                        <span style={{ fontSize: '18px', fontWeight: '700', color: '#667eea' }}>
                          ₹{item.price}
                        </span>
                        
                        {getItemQuantityInCart(item._id) > 0 ? (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          borderRadius: '8px',
                          padding: '5px'
                        }}>
                          <button
                            onClick={() => handleAddToCartWithCheck({...item, quantity: -1})}
                            style={{
                              background: 'rgba(255,255,255,0.2)',
                              border: 'none',
                              borderRadius: '6px',
                              width: '30px',
                              height: '30px',
                              color: 'white',
                              fontSize: '18px',
                              cursor: 'pointer',
                              fontWeight: '700'
                            }}
                          >
                            -
                          </button>
                          <span style={{ color: 'white', fontWeight: '700', minWidth: '20px', textAlign: 'center' }}>
                            {quantityInCart}
                          </span>
                          <button
                            onClick={() => handleAddToCartWithCheck({...item, quantity: 1})}
                            style={{
                              background: 'rgba(255,255,255,0.2)',
                              border: 'none',
                              borderRadius: '6px',
                              width: '30px',
                              height: '30px',
                              color: 'white',
                              fontSize: '18px',
                              cursor: 'pointer',
                              fontWeight: '700'
                            }}
                          >
                            +
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleAddToCartWithCheck({...item, quantity: 1})}
                          style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '8px 20px',
                            color: 'white',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
                          }}
                        >
                          Add +
                        </button>
                      )}
                    </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Cart Summary Button - Only for this store's items */}
      {getStoreCartItems().length > 0 && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1000,
          width: 'calc(100% - 40px)',
          maxWidth: '500px'
        }}>
          <button
            onClick={onCheckout}
            style={{
              width: '100%',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '15px',
              padding: '18px 25px',
              fontSize: '16px',
              fontWeight: '700',
              cursor: 'pointer',
              boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <span>🛒 View Cart ({getStoreCartItems().length} items)</span>
            <span>₹{getCartTotal().toFixed(2)}</span>
          </button>
        </div>
      )}

      {/* Custom Order Floating Button */}
      <button
        onClick={() => setShowCustomOrder(true)}
        style={{
          position: 'fixed',
          bottom: getStoreCartItems().length > 0 ? '90px' : '20px',
          right: '20px',
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
          border: 'none',
          boxShadow: '0 4px 15px rgba(249, 115, 22, 0.4)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '24px',
          zIndex: 999
        }}
        title="Custom Order"
      >
        📝
      </button>

      {/* Custom Order Modal */}
      {showCustomOrder && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '20px',
            padding: '25px',
            maxWidth: '450px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
          }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: '#333' }}>📝 Custom Order</h2>
              <button
                onClick={() => setShowCustomOrder(false)}
                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#666' }}
              >
                ✕
              </button>
            </div>

            <p style={{ color: '#666', fontSize: '14px', marginBottom: '20px' }}>
              Can't find what you need? Tell us what you want and the store will prepare a custom bill for you.
            </p>

            {/* Description */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                📋 Describe what you need:
              </label>
              <textarea
                value={customOrderDescription}
                onChange={(e) => setCustomOrderDescription(e.target.value)}
                placeholder="e.g., I need 2kg rice, 1kg dal, and some vegetables..."
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '10px',
                  border: '1px solid #ddd',
                  fontSize: '14px',
                  minHeight: '80px',
                  resize: 'vertical'
                }}
              />
            </div>

            {/* Item List */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '10px', fontWeight: '600', color: '#333' }}>
                🛒 Or add items individually:
              </label>
              {customItems.map((item, index) => (
                <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '10px', alignItems: 'center' }}>
                  <input
                    type="text"
                    value={item.name}
                    onChange={(e) => updateCustomItem(index, 'name', e.target.value)}
                    placeholder="Item name"
                    style={{
                      flex: 2,
                      padding: '10px',
                      borderRadius: '8px',
                      border: '1px solid #ddd',
                      fontSize: '13px'
                    }}
                  />
                  <input
                    type="text"
                    value={item.quantity}
                    onChange={(e) => updateCustomItem(index, 'quantity', e.target.value)}
                    placeholder="Qty (1kg, 2pcs)"
                    style={{
                      flex: 1,
                      padding: '10px',
                      borderRadius: '8px',
                      border: '1px solid #ddd',
                      fontSize: '13px'
                    }}
                  />
                  {customItems.length > 1 && (
                    <button
                      onClick={() => removeCustomItemRow(index)}
                      style={{
                        background: '#fee2e2',
                        border: 'none',
                        borderRadius: '8px',
                        width: '36px',
                        height: '36px',
                        cursor: 'pointer',
                        color: '#dc2626',
                        fontSize: '16px'
                      }}
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={addCustomItemRow}
                style={{
                  width: '100%',
                  padding: '10px',
                  background: '#f3f4f6',
                  border: '1px dashed #9ca3af',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  color: '#6b7280',
                  fontSize: '14px'
                }}
              >
                + Add another item
              </button>
            </div>

            {/* Notes */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                💬 Special notes (optional):
              </label>
              <input
                type="text"
                value={customOrderNotes}
                onChange={(e) => setCustomOrderNotes(e.target.value)}
                placeholder="e.g., Need fresh items only, call before coming..."
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '10px',
                  border: '1px solid #ddd',
                  fontSize: '14px'
                }}
              />
            </div>

            {/* Info Box */}
            <div style={{
              background: '#fef3c7',
              padding: '15px',
              borderRadius: '10px',
              marginBottom: '20px',
              border: '1px solid #fbbf24'
            }}>
              <p style={{ margin: 0, fontSize: '13px', color: '#92400e' }}>
                ℹ️ <strong>How it works:</strong><br/>
                1. You submit this request<br/>
                2. Store reviews and sends you a bill<br/>
                3. You confirm the bill amount<br/>
                4. Order is processed for delivery
              </p>
            </div>

            {/* Submit Button */}
            <button
              onClick={submitCustomOrder}
              disabled={customOrderSubmitting}
              style={{
                width: '100%',
                padding: '15px',
                background: customOrderSubmitting ? '#9ca3af' : 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '700',
                cursor: customOrderSubmitting ? 'not-allowed' : 'pointer',
                boxShadow: '0 4px 12px rgba(249, 115, 22, 0.3)'
              }}
            >
              {customOrderSubmitting ? '⏳ Sending...' : '📤 Send Custom Order Request'}
            </button>
          </div>
        </div>
      )}

      {/* Category Warning Popup */}
      {showCategoryWarning && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '20px',
            padding: '30px',
            maxWidth: '400px',
            width: '100%',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <span style={{ fontSize: '50px' }}>💡</span>
            </div>
            
            <h3 style={{ 
              margin: '0 0 15px 0', 
              fontSize: '20px', 
              fontWeight: '700', 
              color: '#333',
              textAlign: 'center'
            }}>
              ⚠️ Different Store
            </h3>
            <h4 style={{ 
              margin: '0 0 20px 0', 
              fontSize: '18px', 
              fontWeight: '600', 
              color: '#667eea',
              textAlign: 'center'
            }}>
              వేరే స్టోర్
            </h4>

            <div style={{ 
              background: '#fef3c7', 
              padding: '15px', 
              borderRadius: '12px',
              marginBottom: '16px',
              border: '1px solid #fbbf24'
            }}>
              <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#333', lineHeight: '1.6' }}>
                <strong>📦 You already have items from another store in your cart.</strong>
              </p>
              <p style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#78350f', lineHeight: '1.6' }}>
                ⚡ <strong>Please Note:</strong> Each store requires a separate order with ₹50 delivery charge.
              </p>
              <p style={{ margin: '10px 0 0 0', fontSize: '13px', color: '#15803d', fontStyle: 'italic', lineHeight: '1.5' }}>
                మీ కార్ట్‌లో ఇప్పటికే మరో స్టోర్ నుండి వస్తువులు ఉన్నాయి. ప్రతి స్టోర్‌కు ₹50 డెలివరీ ఛార్జీతో వేరు ఆర్డర్ అవసరం.
              </p>
            </div>

            <div style={{ 
              background: '#f0fdf4', 
              padding: '15px', 
              borderRadius: '12px',
              marginBottom: '20px',
              border: '1px solid #86efac'
            }}>
              <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#15803d', fontWeight: '700', lineHeight: '1.6' }}>
                💡 <strong>Smart Order - Save Money!</strong>
              </p>
              <p style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#166534', lineHeight: '1.6' }}>
                • <strong>2 Separate Orders:</strong> ₹50 + ₹50 = ₹100<br/>
                • <strong>1 Combined Order:</strong> Only ₹70 delivery<br/>
              </p>
              <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#15803d', fontWeight: '700' }}>
                💰 You save ₹30 on delivery!
              </p>
              <p style={{ margin: '12px 0 0 0', fontSize: '13px', color: '#15803d', fontStyle: 'italic', lineHeight: '1.5' }}>
                <strong>స్మార్ట్ ఆర్డర్ - డబ్బు ఆదా చేయండి!</strong><br/>
                • 2 వేరు ఆర్డర్లు: ₹50 + ₹50 = ₹100<br/>
                • 1 కలిపి ఆర్డర్: కేవలం ₹70 డెలివరీ<br/>
                💰 మీరు ₹30 ఆదా చేస్తారు!
              </p>
            </div>

            <p style={{ margin: '0 0 20px 0', fontSize: '13px', color: '#666', textAlign: 'center', lineHeight: '1.5' }}>
              Our partner will collect from both stores in one trip!<br/>
              <span style={{ fontSize: '12px' }}>మా పార్ట్‌నర్ ఒకే ట్రిప్‌లో రెండు స్టోర్ల నుండి సేకరిస్తారు!</span>
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button
                onClick={handleConfirmAddCategory}
                style={{
                  width: '100%',
                  padding: '14px',
                  background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '15px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3)'
                }}
              >
                💰 Combine Order (₹70) • కలిపి ఆర్డర్ చేయండి
              </button>
              <button
                onClick={handleSeparateOrder}
                style={{
                  width: '100%',
                  padding: '14px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                }}
              >
                📦 Separate Order (₹100) • వేరు ఆర్డర్ చేయండి
              </button>
              <button
                onClick={handleCancelAddCategory}
                style={{
                  width: '100%',
                  padding: '14px',
                  background: '#f3f4f6',
                  color: '#6b7280',
                  border: '1px solid #d1d5db',
                  borderRadius: '12px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                ❌ Cancel • రద్దు చేయండి
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StorePage;
